"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PACKAGE_LINES_KEY = exports.EQUIPMENT_LINES_KEY = void 0;
exports.equipmentScheduleRows = equipmentScheduleRows;
exports.packageScheduleRows = packageScheduleRows;
exports.scheduleColumnChoices = scheduleColumnChoices;
exports.composeMergeData = composeMergeData;
const customer_1 = require("./customer");
const finance_1 = require("./finance");
const equipment_1 = require("./equipment");
const device_1 = require("./device");
const packages_1 = require("./packages");
const party_1 = require("./party");
const counterparties_1 = require("./counterparties");
/**
 * ONE PLACE THAT TURNS PROVIDER OUTPUT INTO `{{token}}` NAMES.
 *
 * Every provider returns keys WITHOUT their prefix (`legal_name`, not
 * `vendor_legal_name`) because a provider does not know what a document calls
 * it. The catalog knows: `vendor_legal_name` resolves to
 * `{ provider: 'vendor', key: 'legal_name' }`. This function is the other half
 * of that mapping — the one the services use — so the prefix rule is written
 * down once instead of in every service that composes an envelope.
 *
 * Before this existed, billing-service hand-wrote `put('equipment_summary',
 * eq.summary)` one line per field, and each new envelope creator would have
 * hand-written its own. Two hand-maintained copies of a naming rule is the
 * drift the catalog exists to eliminate.
 *
 * UNDEFINED VALUES ARE OMITTED, never emitted as `''`. A key present with a
 * blank value is exactly the failure `MissingMergeFieldError` exists to catch,
 * and omitting it lets the renderer's own missing-field rule do its job.
 */
/** The JSON-encoded row source a `scheduleTable` reads. */
exports.EQUIPMENT_LINES_KEY = 'equipment_lines_json';
/** The JSON-encoded row source a `scheduleTable` reads. */
exports.PACKAGE_LINES_KEY = 'packages_json';
/**
 * The rows a schedule table draws, as flat string cells keyed by the same
 * column names the embed names. Built here rather than in the renderer so the
 * schedule and the singular `{{device_model}}` token are formatted by ONE set
 * of provider functions and cannot disagree about how a price is written.
 */
function equipmentScheduleRows(lines) {
    return lines.map((line) => {
        // Each row is the ONE-LINE case of the device provider, which is exactly
        // what a schedule row is: everything singular is true about this row.
        const v = (0, device_1.deviceValues)([line]);
        const row = {};
        for (const [k, val] of Object.entries(v))
            if (val !== undefined)
                row[k] = val;
        return row;
    });
}
function packageScheduleRows(lines) {
    return lines.map((line) => {
        const v = (0, packages_1.packageValues)([line]);
        const row = {};
        for (const [k, val] of Object.entries(v))
            if (val !== undefined)
                row[k] = val;
        return row;
    });
}
/**
 * THE COLUMNS AN AUTHOR MAY PUT IN A SCHEDULE, with the labels the palette
 * already uses.
 *
 * Derived from the catalog rather than hand-listed, so a field added to the
 * `device` or `package` group becomes an available column with no second edit —
 * and a column can never name a cell the row source does not produce, because
 * both come from the same provider key space.
 *
 * The catalog is passed IN rather than imported, so this module stays free of
 * the import cycle `mergeFieldCatalog` → providers → here would otherwise
 * create. `mergeFieldsByGroup()` is the natural argument.
 */
function scheduleColumnChoices(source, byGroup) {
    const group = source === 'equipment' ? 'device' : 'package';
    const prefix = `${group}_`;
    return (byGroup[group] ?? [])
        .filter((d) => d.name.startsWith(prefix))
        .map((d) => ({ key: d.name.slice(prefix.length), label: d.label }));
}
function composeMergeData(input) {
    const out = {};
    const put = (name, v) => { if (v !== undefined && v !== '')
        out[name] = v; };
    const putAll = (prefix, values) => {
        for (const [key, v] of Object.entries(values))
            put(`${prefix}_${key}`, v);
    };
    // Company values arrive already prefixed — they come from the Company
    // Identity Master reader, which is the one provider a service reads whole.
    for (const [k, v] of Object.entries(input.company ?? {}))
        put(k, v);
    if (input.customer) {
        putAll('customer', (0, customer_1.customerValues)(input.customer.tenant ?? {}, input.customer.location ?? {}));
    }
    if (input.signer)
        putAll('signer', (0, party_1.signerValues)(input.signer));
    if (input.guarantor)
        putAll('guarantor', (0, party_1.guarantorValues)(input.guarantor));
    if (input.finance)
        putAll('finance', (0, finance_1.financeValues)(input.finance));
    if (input.vendor)
        putAll('vendor', (0, counterparties_1.vendorValues)(input.vendor));
    if (input.employee)
        putAll('employee', (0, counterparties_1.employeeValues)(input.employee));
    if (input.partner)
        putAll('partner', (0, counterparties_1.partnerValues)(input.partner));
    if (input.devices) {
        putAll('device', (0, device_1.deviceValues)(input.devices));
        // The roll-up shares the same lines, so `equipment_summary` and
        // `device_model` can never describe different orders.
        putAll('equipment', (0, equipment_1.equipmentValues)(input.devices.map((d) => ({
            name: d.bundleName || d.modelName || 'Device',
            quantity: d.quantity,
            unitPriceCents: d.unitPriceCents,
        }))));
        if (input.devices.length) {
            put(exports.EQUIPMENT_LINES_KEY, JSON.stringify(equipmentScheduleRows(input.devices)));
        }
    }
    if (input.packages) {
        putAll('package', (0, packages_1.packageValues)(input.packages));
        if (input.packages.length) {
            put(exports.PACKAGE_LINES_KEY, JSON.stringify(packageScheduleRows(input.packages)));
        }
    }
    if (input.deal) {
        put('deal_name', input.deal.name);
        put('deal_code', input.deal.code);
    }
    // `effective_date` is deliberately NOT prefixed — 15 already-seeded documents
    // reference it by that exact name. The catalog says so; this honours it.
    if (input.agreement) {
        const a = (0, party_1.agreementValues)(input.agreement);
        put('agreement_no', a.no);
        put('agreement_date', a.date);
        put('effective_date', a.effective_date);
        put('agreement_execution_date', a.execution_date);
        put('agreement_execution_time', a.execution_time);
        put('agreement_customer_signed_date', a.customer_signed_date);
        put('agreement_company_signed_date', a.company_signed_date);
        put('agreement_guarantor_signed_date', a.guarantor_signed_date);
    }
    return out;
}
//# sourceMappingURL=compose.js.map